import type { ProjectState, StatsFields } from "@livcg/schema";
import { RefreshCw, RotateCcw } from "lucide-react";
import { useEffect, useState } from "react";
import { send, sendRaw } from "../shared/sendRaw.js";
import { Button, Card, cn, Stepper, useToast } from "../ui/index.js";
import { StatsApiLink } from "./StatsApiLink.js";

type Field = { key: keyof StatsFields; label: string; max?: number; step?: number; auto?: boolean };

const FIELDS: Field[] = [
  { key: "passes_completed", label: "Aniq uzatmalar" },
  { key: "passes_incompleted", label: "Noaniq uzatmalar" },
  { key: "passes", label: "Umumiy paslar", auto: true },
  { key: "passes_accuracy", label: "Pass aniqligi %", max: 100, auto: true },
  { key: "ball_control", label: "To'p nazorati %", max: 100 },
  { key: "tackles", label: "To'pni olib qo'yish" },
  { key: "shots_on_target", label: "Aniq zarbalar" },
  { key: "shots_off_target", label: "Aniqsiz zarbalar" },
  { key: "shots", label: "Zarbalar", auto: true },
  { key: "shots_accuracy", label: "Zarba aniqligi %", max: 100, auto: true },
  { key: "key_passes", label: "Kalit passlar" },
  { key: "saves", label: "Seyvlar" },
  { key: "corners", label: "Burchak zarbalari" },
  { key: "touches", label: "To'pga teginish" },
  { key: "fouls", label: "Qoidabuzarliklar" },
  // xG — the only fractional field (shown as 0.9 / 1.58 in the stats overlay).
  { key: "xg", label: "xG", max: 99, step: 0.01 },
  { key: "offsides", label: "Ofsaydlar" },
  // Cards are NOT EDITED here — the server derives them from the event
  // log (recomputeCards). A manual change would be overwritten on the next
  // card event. Yellow/red go through QuickEvents + EventLog.
];

function decimalsOf(step: number): number {
  return (String(step).split(".")[1] ?? "").length;
}

const REFRESH_ERRORS: Record<string, string> = {
  match_unmapped: "dostats o'yini tanlanmagan — yuqorida bog'lang.",
  no_data: "dostats'da bu o'yin uchun hali ma'lumot yo'q.",
  dostats_disabled: "dostats o'chiq — statistika faqat qo'lda.",
};

function ago(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  return s < 60 ? `${s} s oldin` : `${Math.floor(s / 60)} daq ${s % 60} s oldin`;
}

/** "Yangilash" — pulls dostats once and shows how long ago it last did. */
function StatsRefresh({ slug, fetchedAt }: { slug: string; fetchedAt: number | null }) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!fetchedAt) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [fetchedAt]);

  const refresh = async () => {
    setBusy(true);
    try {
      const r = (await sendRaw("stats.refresh", { slug })) as
        | { ok: true }
        | { ok: false; error: string; issues?: unknown };
      if (r.ok) toast({ tone: "success", title: "Statistika yangilandi" });
      else
        toast({
          tone: "error",
          title: "Statistika kelmadi",
          description:
            REFRESH_ERRORS[r.error] ??
            `dostats javob bermadi${typeof r.issues === "string" ? `: ${r.issues}` : ""}`,
        });
    } catch (e) {
      toast({ tone: "error", title: "Yuborib bo'lmadi", description: (e as Error).message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[12px] text-ink-faint">
        Oxirgi yangilash: {fetchedAt ? ago(now - fetchedAt) : "hali yo'q"}
      </span>
      <Button variant="primary" size="sm" disabled={busy} onClick={() => void refresh()}>
        <RefreshCw className={cn("h-3.5 w-3.5", busy && "animate-spin")} strokeWidth={2.5} />
        Statsni yangilash
      </Button>
    </div>
  );
}

type Props = { slug: string; state: ProjectState };
type Side = "home" | "away";

/** A field the operator corrected: dostats no longer overwrites it. The
 *  button puts it back on the API value. */
function SideCell({
  side,
  field,
  value,
  manual,
  apiValue,
  onChange,
  onReset,
}: {
  side: Side;
  field: Field;
  value: number;
  manual: boolean;
  apiValue: number | undefined;
  onChange: (v: number) => void;
  onReset: () => void;
}) {
  const reset = manual ? (
    <Button
      variant="ghost"
      size="xs"
      iconOnly
      aria-label="API qiymatiga qaytarish"
      title={apiValue !== undefined ? `API: ${apiValue} — qaytarish` : "API'ga qaytarish"}
      onClick={onReset}
    >
      <RotateCcw className="h-3 w-3" />
    </Button>
  ) : (
    <span className="w-6" />
  );
  return (
    <div
      className={cn("flex items-center gap-1", side === "home" ? "justify-start" : "justify-end")}
    >
      {side === "away" && reset}
      <Stepper
        value={value}
        {...(field.max !== undefined ? { max: field.max } : {})}
        {...(field.step !== undefined ? { step: field.step } : {})}
        onChange={onChange}
        size="sm"
        className={cn(manual && "rounded-md ring-1 ring-warning/60")}
      />
      {side === "home" && reset}
    </div>
  );
}

export function TabStats({ slug, state }: Props) {
  const homeStats = state.match.homeStats;
  const awayStats = state.match.awayStats;
  const home = state.teams.home;
  const away = state.teams.away;

  // Optimistic: a click shows at once and the next click builds on it, instead
  // of waiting for the replicant round trip (fast clicks were lost).
  const [pending, setPending] = useState<Record<string, number>>({});
  useEffect(() => {
    setPending((cur) => {
      const left = Object.entries(cur).filter(([k, v]) => {
        const [side, key] = k.split(":") as [Side, keyof StatsFields];
        return (side === "home" ? homeStats : awayStats)[key] !== v;
      });
      return left.length === Object.keys(cur).length ? cur : Object.fromEntries(left);
    });
  }, [homeStats, awayStats]);
  const shown = (side: Side, key: keyof StatsFields) =>
    pending[`${side}:${key}`] ?? Number((side === "home" ? homeStats : awayStats)[key] ?? 0);

  const patch = (side: Side, field: Field, value: number) => {
    const clamped = Math.max(0, Math.min(field.max ?? 9999, value));
    const v = field.step ? Number(clamped.toFixed(decimalsOf(field.step))) : Math.floor(clamped);
    const k = `${side}:${field.key}`;
    setPending((cur) => ({ ...cur, [k]: v }));
    send("stats.patch", { slug, side, stats: { [field.key]: v } });
    // Never stick if the server keeps a different value.
    setTimeout(
      () =>
        setPending((cur) => {
          if (cur[k] !== v) return cur;
          const { [k]: _, ...rest } = cur;
          return rest;
        }),
      2000,
    );
  };

  const manual = {
    home: new Set(state.match.statsManual?.home ?? []),
    away: new Set(state.match.statsManual?.away ?? []),
  };
  const api = state.match.statsApi;
  const reset = (side: Side, keys?: string[]) =>
    send("stats.reset", { slug, side, ...(keys ? { keys } : {}) });
  const anyManual = manual.home.size + manual.away.size > 0;

  return (
    <Card
      title="Statistika"
      subtitle="Har stats overlay'ga jonli ulanadi"
      action={
        <div className="flex items-center gap-4 t-mono">
          <span>{home?.shortName ?? "HOME"}</span>
          <span className="text-ink-faint">vs</span>
          <span>{away?.shortName ?? "AWAY"}</span>
        </div>
      }
    >
      <div className="grid gap-2">
        <StatsApiLink matchId={state.matchId} />
        <StatsRefresh slug={slug} fetchedAt={state.match.statsFetchedAt ?? null} />
        <p className="text-[11px] text-ink-faint">
          Statsni yangilash dostats'dan oladi, 0 qiymatlar olinmaydi. Qo'lda o'zgartirilgan qiymat{" "}
          <span className="text-warning">sariq</span> chegarada — Yangilash uni yozmaydi.{" "}
          <RotateCcw className="inline h-3 w-3" /> tugmasi API qiymatiga qaytaradi.
        </p>
        {anyManual && (
          <div className="flex justify-between">
            <Button
              variant="ghost"
              size="xs"
              disabled={manual.home.size === 0}
              onClick={() => reset("home")}
            >
              Uy: hammasini API'ga ({manual.home.size})
            </Button>
            <Button
              variant="ghost"
              size="xs"
              disabled={manual.away.size === 0}
              onClick={() => reset("away")}
            >
              Mehmon: hammasini API'ga ({manual.away.size})
            </Button>
          </div>
        )}
        {FIELDS.map((f) => (
          <div
            key={f.key}
            className="grid grid-cols-[1fr_2fr_1fr] items-center gap-3 rounded-md border border-divider bg-surface-muted/40 px-3 py-2"
          >
            <SideCell
              side="home"
              field={f}
              value={shown("home", f.key)}
              manual={manual.home.has(f.key)}
              apiValue={api?.home[f.key]}
              onChange={(v) => patch("home", f, v)}
              onReset={() => reset("home", [f.key])}
            />
            <div className="text-center text-[13px] text-ink-soft">
              {f.label}
              {f.auto && <span className="ml-1.5 text-[10px] uppercase text-ink-faint">avto</span>}
            </div>
            <SideCell
              side="away"
              field={f}
              value={shown("away", f.key)}
              manual={manual.away.has(f.key)}
              apiValue={api?.away[f.key]}
              onChange={(v) => patch("away", f, v)}
              onReset={() => reset("away", [f.key])}
            />
          </div>
        ))}
      </div>
    </Card>
  );
}
